import { User } from "@/types/v2";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Button } from "@/components/Base/Button";
import { Checkbox } from "@/components/Base/Checkbox";

const userEditSchema = z.object({
  displayName: z.string().optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  emailVerified: z.boolean(),
  stripeCustomerId: z.string().optional(),
});

type UserEditFormValues = z.infer<typeof userEditSchema>;

export type UserEditFormProps = {
  user: User;
  onSubmit: (data: UserEditFormValues) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
};

export const UserEditForm = ({
  user,
  onSubmit,
  onCancel,
  isSubmitting,
}: UserEditFormProps) => {
  const form = useForm<UserEditFormValues>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      displayName: user.displayName || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      emailVerified: user.emailVerified || false,
      stripeCustomerId: user.stripeCustomerId || "",
    },
  });

  const handleSubmit = async (data: UserEditFormValues) => {
    console.log(data);
    await onSubmit(data);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="grid gap-4 py-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="First Name"
                    disabled={form.formState.isSubmitting || isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Last Name"
                    disabled={form.formState.isSubmitting || isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Name</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Display Name"
                    disabled={form.formState.isSubmitting || isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormItem>
            <FormLabel>Email</FormLabel>
            <Input type="text" value={user.email || ""} disabled />
          </FormItem>

          <FormField
            control={form.control}
            name="emailVerified"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(val) => {
                      field.onChange(val === true);
                    }}
                    disabled={form.formState.isSubmitting || isSubmitting}
                    className={"border-primary"}
                  />
                </FormControl>
                <FormLabel>Email Verified</FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="stripeCustomerId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Stripe Customer ID</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="cus_..."
                    disabled={form.formState.isSubmitting || isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-row justify-end gap-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="accent"
            disabled={
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              isSubmitting
            }
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
